import { useQueryClient } from '@tanstack/react-query';

import { useSecretMutation } from '@/hooks/use-secret-mutation';

import { enrollmentService } from '@/services/enrollment-service';
import type {
  IssueJoinCodePayload,
  IssueJoinCodeResult,
} from '@/types/core/enrollment';
import { pollForResource } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { enrollmentMutationKeys, enrollmentQueryKeys } from './query-keys';

/**
 * Mint a single-use join code. The code is returned once. Waits for the read
 * model before resolving so the new credential is visible in the listing on
 * success.
 */
export function useIssueEnrollmentJoinCode(tenantId?: UUID) {
  const qc = useQueryClient();

  return useSecretMutation<IssueJoinCodeResult, IssueJoinCodePayload>({
    mutationKey: enrollmentMutationKeys.issueJoinCode(tenantId),
    mutationFn: async (payload) => {
      if (!tenantId) throw new Error('Missing tenant id');

      const result = await enrollmentService.issueJoinCode(tenantId, payload);

      // Reads are eventually consistent; wait for the new credential to appear in
      // the read model before resolving. Otherwise the invalidation-triggered
      // refetch can land before it is projected and "Outstanding Join Codes"
      // shows nothing — hiding a live, unexpired code that can then never be
      // found or cancelled.
      await pollForResource(async () => {
        const credentials = await enrollmentService.listCredentials(tenantId);
        return (
          credentials.find((c) => c.credentialId === result.credentialId) ??
          null
        );
      });

      return result;
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: enrollmentQueryKeys.credentialsByTenant(tenantId),
        refetchType: 'all',
      });
    },
  });
}
